/**
 * CondensedComposer — "Nova mensagem" DENTRO do popover condensado
 * (Mensageria Condensada · superfície 4 · PR F1b).
 *
 * Não é modal de tela cheia — é a view `compose` do popover (P13). Fluxo:
 *   1. Destinatário — toggle pessoa × time (time só p/ quem pode iniciar descendente
 *      + broadcast). OPERADOR (só `direct-message-initiate-upward`): sem toggle,
 *      fixo em pessoa, com nota de escopo ("você fala só com seu supervisor/líder").
 *   2. Picker escopado — REUSA as MESMAS fontes do SendDirectMessageModal de
 *      /mensagens (`usePresence` p/ pessoas, `useTeamsList` p/ times). O backend
 *      resolve a DIREÇÃO (ascendente p/ operador, descendente p/ supervisor) pela
 *      capability — o FE só envia `toUserId`. Alvo fora de escopo → 403 mapeado em
 *      linha honesta inline (não toast), igual ao modal.
 *   3. Broadcast (só time): canal × individual.
 *   4. Âncora de 1 toque — quando aberto DE DENTRO de uma Conversa em curso
 *      (`useMatch('/atendimento/conversation/:id')`), pré-resolve "Anexar esta
 *      conversa" (toggle ON por default). Fora de uma conversa → toggle ausente.
 *   5. Mensagem + enviar. Validação honesta: sem destinatário/sem texto → Enviar
 *      desabilitado + mensagem específica inline.
 *
 * Mesmos contratos do modal (reuso de hooks `useInitiateThread`/`useBroadcast`):
 *   1:1 → POST /threads { toUserId, body, anchorConversationId? }
 *   broadcast → POST /broadcast { teamId, body, mode, anchorConversationId? }
 */

import { Anchor, Check, ChevronLeft, Lock, Search, Send, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { useConversation } from '../../../../hooks/use-conversation';
import { ApiError } from '../../../../lib/api-client';
import { avatarColorFor, initialsOf } from '../../../../lib/format';
import { useBroadcast, useInitiateThread, useRecipientScope } from '../../hooks/use-direct-messages';

import type { BroadcastBody, BroadcastMode, InitiateThreadBody } from '../../types';

type RecipientTab = 'pessoa' | 'time';

interface CondensedComposerProps {
  onBack: () => void;
  /**
   * Sucesso 1:1 → abre o thread recém-criado (o popover navega para ele). Broadcast
   * não tem thread único, então cai no `onBack` (volta à lista). Sem `onSent`, ambos
   * caem no `onBack`.
   */
  onSent?: (threadId: string) => void;
}

/** Copy honesto por código de erro 403 do backend; espelha o do SendDirectMessageModal. */
function errorMessageFor(code: string | undefined): string {
  switch (code) {
    case 'DM_TARGET_OUT_OF_SCOPE':
      return 'Essa pessoa está fora do seu escopo — você só inicia para quem te supervisiona ou supervisiona.';
    case 'DM_TEAM_OUT_OF_SCOPE':
      return 'Esse time está fora do seu escopo de supervisão.';
    case 'DM_EMPTY_TEAM':
      return 'Esse time não tem outros membros para receber a mensagem.';
    case 'DM_ANCHOR_OUT_OF_TENANT':
      return 'A conversa anexada não está disponível.';
    default:
      return 'Não foi possível enviar a mensagem. Tente novamente.';
  }
}

function codeOf(err: unknown): string | undefined {
  return err instanceof ApiError ? err.body.code : undefined;
}

export function CondensedComposer({ onBack, onSent }: CondensedComposerProps): JSX.Element {
  // Direção resolvida por capability no backend: o FE só ramifica a UI. O supervisor
  // (`direct-message-initiate`) inicia descendente + broadcast (toggle de time); o
  // operador (`direct-message-initiate-upward`) inicia só ascendente (sem time).
  const canInitiateDown = useCapability('direct-message-initiate');
  const canTeam = canInitiateDown;
  // Mensageria lateral/peer (opt-in via additionalCapabilities) — só amplia a aba
  // "pessoa"; broadcast (toggle de time) segue gateado por canInitiateDown.
  const canLateral = useCapability('direct-message-initiate-lateral');
  const canAnyone = useCapability('direct-message-initiate-anyone');

  // Picker escopado: o diretório de destinatários (com nomes) vem do backend —
  // só quem o ator pode INICIAR. Autossuficiente; não depende de presença.
  const scope = useRecipientScope();

  // Âncora de 1 toque: a conversa atual vem da rota (o popover é global na topbar).
  const convMatch = useMatch('/atendimento/conversation/:id');
  const currentConversationId = convMatch?.params.id ?? null;
  const { data: currentConversation } = useConversation(currentConversationId ?? undefined);

  const initiate = useInitiateThread();
  const broadcast = useBroadcast();

  const [tab, setTab] = useState<RecipientTab>('pessoa');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [mode, setMode] = useState<BroadcastMode>('group');
  // Âncora pré-resolvida: ON por default quando há conversa atual.
  const [anchorOn, setAnchorOn] = useState(currentConversationId !== null);
  const [body, setBody] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Busca do picker (pessoa OU time conforme a aba). Filtro client-side por
  // substring case-insensitive no nome de exibição.
  const [query, setQuery] = useState('');

  const recipTab: RecipientTab = canTeam ? tab : 'pessoa';

  const people = scope.people;
  const scopedTeams = useMemo(
    () => scope.teams.map((t) => ({ id: t.teamId, name: t.name, memberCount: t.memberCount })),
    [scope.teams],
  );

  const needle = query.trim().toLowerCase();
  // Mantém o destinatário selecionado SEMPRE visível, mesmo quando a busca não o
  // casa: assim o "Pronto para enviar" nunca aponta para um alvo invisível. Se o
  // selecionado caiu fora do filtro, fixamos a linha no topo (resolvendo display
  // da fonte completa). O filtro case-insensitive segue valendo p/ o resto.
  const filteredPeople = useMemo(() => {
    const base = needle.length === 0 ? people : people.filter((p) => p.name.toLowerCase().includes(needle));
    if (selectedUserId === null || base.some((p) => p.userId === selectedUserId)) return base;
    const selected = people.find((p) => p.userId === selectedUserId);
    return selected !== undefined ? [selected, ...base] : base;
  }, [people, needle, selectedUserId]);
  const filteredTeams = useMemo(() => {
    const base =
      needle.length === 0 ? scopedTeams : scopedTeams.filter((t) => t.name.toLowerCase().includes(needle));
    if (selectedTeamId === null || base.some((t) => t.id === selectedTeamId)) return base;
    const selected = scopedTeams.find((t) => t.id === selectedTeamId);
    return selected !== undefined ? [selected, ...base] : base;
  }, [scopedTeams, needle, selectedTeamId]);

  const anchorName =
    currentConversation?.person?.displayName ?? '(conversa sem identidade)';
  const hasAnchorOption = currentConversationId !== null;
  const effectiveAnchor = hasAnchorOption && anchorOn ? currentConversationId : undefined;

  const trimmedBody = body.trim();
  const hasRecipient = recipTab === 'pessoa' ? selectedUserId !== null : selectedTeamId !== null;
  const isPending = initiate.isPending || broadcast.isPending;
  const canSubmit = hasRecipient && trimmedBody.length > 0 && !isPending;

  const validationHint = !hasRecipient
    ? 'Escolha um destinatário'
    : trimmedBody.length === 0
      ? 'Escreva uma mensagem'
      : 'Pronto para enviar';

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    setErrorMsg(null);

    if (recipTab === 'pessoa' && selectedUserId !== null) {
      const payload: InitiateThreadBody = {
        toUserId: selectedUserId,
        body: trimmedBody,
        ...(effectiveAnchor !== undefined ? { anchorConversationId: effectiveAnchor } : {}),
      };
      initiate.mutate(payload, {
        onSuccess: (res) => {
          // 1:1 → abre o thread novo; sem handler de navegação, volta à lista.
          if (onSent !== undefined) onSent(res.threadId);
          else onBack();
        },
        onError: (err: unknown) => {
          setErrorMsg(errorMessageFor(codeOf(err)));
        },
      });
      return;
    }

    if (recipTab === 'time' && selectedTeamId !== null) {
      const payload: BroadcastBody = {
        teamId: selectedTeamId,
        mode,
        body: trimmedBody,
        ...(effectiveAnchor !== undefined ? { anchorConversationId: effectiveAnchor } : {}),
      };
      broadcast.mutate(payload, {
        // Broadcast não tem thread único → sempre volta à lista (sem `onSent`).
        onSuccess: () => {
          onBack();
        },
        onError: (err: unknown) => {
          setErrorMsg(errorMessageFor(codeOf(err)));
        },
      });
    }
  };

  return (
    <div className="mc-view mc-scroll mc-view--scroll">
      <div className="mc-subheader mc-subheader--sticky">
        <button type="button" className="mc-icon-btn" aria-label="Voltar" onClick={onBack}>
          <ChevronLeft size={15} strokeWidth={1.7} aria-hidden />
        </button>
        <b className="mc-subheader-plain">Nova mensagem</b>
      </div>

      <div className="mc-compose-body">
        {/* 1 · Destinatário */}
        <div className="mc-field">
          <span className="mc-eyebrow">Para</span>
          {canTeam ? (
            <div className="mc-segmented" role="tablist" aria-label="Tipo de destinatário">
              <button
                type="button"
                role="tab"
                aria-selected={recipTab === 'pessoa'}
                className={`mc-seg${recipTab === 'pessoa' ? ' is-on' : ''}`}
                onClick={() => {
                  setTab('pessoa');
                  setQuery('');
                }}
              >
                Uma pessoa
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={recipTab === 'time'}
                className={`mc-seg${recipTab === 'time' ? ' is-on' : ''}`}
                onClick={() => {
                  setTab('time');
                  setQuery('');
                }}
              >
                Um time
              </button>
            </div>
          ) : (
            <div className="mc-scope-note">
              <Lock size={12} strokeWidth={1.6} aria-hidden />
              {canAnyone
                ? 'Você pode iniciar conversa com qualquer pessoa da operação'
                : canLateral
                  ? 'Você fala com colegas dos seus times e com seu supervisor'
                  : 'Você fala apenas com seu supervisor ou líder de time'}
            </div>
          )}
        </div>

        {recipTab === 'pessoa' ? (
          <div className="mc-field">
            <div className="mc-search">
              <Search size={14} strokeWidth={1.5} aria-hidden />
              <input
                type="text"
                className="mc-search-input"
                value={query}
                placeholder="Pessoas no seu escopo"
                aria-label="Buscar pessoas no seu escopo"
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
              />
            </div>
            <div className="mc-picker" role="listbox" aria-label="Pessoas">
              {filteredPeople.length === 0 ? (
                <span className="mc-scope-note">
                  {scope.isLoading
                    ? 'Carregando seu escopo…'
                    : needle.length === 0
                      ? 'Nenhuma pessoa disponível no seu escopo.'
                      : 'Nenhuma pessoa encontrada para essa busca.'}
                </span>
              ) : (
                filteredPeople.map((p) => {
                  const sel = p.userId === selectedUserId;
                  return (
                    <button
                      key={p.userId}
                      type="button"
                      role="option"
                      aria-selected={sel}
                      className={`mc-picker-row${sel ? ' is-selected' : ''}`}
                      onClick={() => {
                        setSelectedUserId(p.userId);
                      }}
                    >
                      <span
                        className="mc-avatar mc-avatar--sm"
                        aria-hidden
                        style={{ background: avatarColorFor(p.userId) }}
                      >
                        {initialsOf(p.name)}
                      </span>
                      <span className="mc-picker-text">
                        <b>{p.name}</b>
                      </span>
                      {sel && (
                        <Check size={15} strokeWidth={2} className="mc-picker-check" aria-hidden />
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {/* Limite de escopo: honesto, não erro genérico. Omitido quando o ator
                alcança qualquer pessoa (anyone) — não há "fora do escopo". */}
            {!canAnyone && (
            <div className="mc-scope-limit">
              <Lock size={14} strokeWidth={1.5} aria-hidden />
              <span className="mc-picker-text">
                <b>Fora do seu escopo</b>
                <span>
                  {canInitiateDown
                    ? 'Pessoas fora do seu time/escopo de supervisão não aparecem na busca.'
                    : canLateral
                      ? 'Quem não é do seu time nem te supervisiona não aparece na busca.'
                      : 'Diretoria e outros times não aparecem — você só inicia para cima, com quem te supervisiona.'}
                </span>
              </span>
            </div>
            )}
          </div>
        ) : (
          <div className="mc-field">
            <div className="mc-search">
              <Search size={14} strokeWidth={1.5} aria-hidden />
              <input
                type="text"
                className="mc-search-input"
                value={query}
                placeholder="Times no seu escopo"
                aria-label="Buscar times no seu escopo"
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
              />
            </div>
            <div className="mc-picker" role="listbox" aria-label="Times">
              {filteredTeams.length === 0 ? (
                <span className="mc-scope-note">
                  {scope.isLoading
                    ? 'Carregando seu escopo…'
                    : needle.length === 0
                      ? 'Nenhum time que você lidera.'
                      : 'Nenhum time encontrado para essa busca.'}
                </span>
              ) : (
                filteredTeams.map((t) => {
                  const sel = t.id === selectedTeamId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="option"
                      aria-selected={sel}
                      className={`mc-picker-row${sel ? ' is-selected' : ''}`}
                      onClick={() => {
                        setSelectedTeamId(t.id);
                      }}
                    >
                      <span className="mc-avatar mc-avatar--sm mc-avatar--team" aria-hidden>
                        <Users size={14} strokeWidth={1.6} />
                      </span>
                      <span className="mc-picker-text">
                        <b>{t.name}</b>
                        <span>{`${t.memberCount.toString()} pessoas`}</span>
                      </span>
                      {sel && (
                        <Check size={15} strokeWidth={2} className="mc-picker-check" aria-hidden />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {selectedTeamId !== null && (
              <>
                <span className="mc-eyebrow">Como enviar</span>
                <button
                  type="button"
                  className={`mc-radio${mode === 'group' ? ' is-on' : ''}`}
                  aria-pressed={mode === 'group'}
                  onClick={() => {
                    setMode('group');
                  }}
                >
                  <span className="mc-radio-dot" aria-hidden />
                  <span className="mc-radio-text">
                    <b>Canal do time</b>
                    <span>Todos veem e podem responder no mesmo fio</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`mc-radio${mode === 'individual' ? ' is-on' : ''}`}
                  aria-pressed={mode === 'individual'}
                  onClick={() => {
                    setMode('individual');
                  }}
                >
                  <span className="mc-radio-dot" aria-hidden />
                  <span className="mc-radio-text">
                    <b>Aviso individual</b>
                    <span>Cada pessoa recebe em separado, sem ver as outras</span>
                  </span>
                </button>
              </>
            )}
          </div>
        )}

        {/* 4 · Âncora opcional de 1 toque — só quando há conversa atual */}
        {hasAnchorOption && (
          <div className="mc-field">
            <span className="mc-eyebrow">
              Contexto <i>· opcional</i>
            </span>
            <button
              type="button"
              className={`mc-anchor-toggle${anchorOn ? ' is-on' : ''}`}
              aria-pressed={anchorOn}
              onClick={() => {
                setAnchorOn((v) => !v);
              }}
            >
              <span className="mc-context-glyph" aria-hidden>
                <Anchor size={13} strokeWidth={1.5} />
              </span>
              <span className="mc-picker-text">
                <b>Anexar esta conversa</b>
                <span>{anchorName} (a tela atual)</span>
              </span>
              <span className="mc-switch" aria-hidden>
                <span className="mc-switch-knob" />
              </span>
            </button>
          </div>
        )}

        {/* 5 · Mensagem */}
        <div className="mc-field">
          <label className="mc-eyebrow" htmlFor="mc-compose-body">
            Mensagem
          </label>
          <textarea
            id="mc-compose-body"
            className="mc-textarea mc-textarea--lg"
            rows={3}
            value={body}
            placeholder="Escreva sua mensagem…"
            aria-label="Mensagem"
            onChange={(e) => {
              setBody(e.target.value);
            }}
          />
        </div>

        {errorMsg !== null && (
          <div className="mc-scope-limit" role="alert">
            <Lock size={14} strokeWidth={1.5} aria-hidden />
            <span className="mc-picker-text">
              <b>Não enviado</b>
              <span>{errorMsg}</span>
            </span>
          </div>
        )}

        <div className="mc-send-row">
          <button
            type="button"
            className="mc-primary-btn"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isPending ? 'Enviando…' : 'Enviar mensagem'}
            <Send size={13} strokeWidth={1.8} aria-hidden />
          </button>
          <span className="mc-send-hint">{validationHint}</span>
        </div>
      </div>
    </div>
  );
}
